import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { RISK, fmtEur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Activity, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ScenarioScoreChart } from "@/components/ReportCharts";

function GradePill({ grade }) {
  const r = RISK[grade] || RISK.C;
  return <span className={`inline-flex items-center justify-center h-7 w-7 rounded-md font-heading font-bold text-white text-sm ${r.solid}`}>{grade}</span>;
}

function DeltaChip({ v }) {
  if (v === null || v === undefined) return <span className="text-slate-500 text-xs">—</span>;
  const pos = v >= 0;
  const Icon = v === 0 ? Minus : pos ? TrendingUp : TrendingDown;
  const cls = v === 0 ? "text-slate-500" : pos ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono-fin ${cls}`}>
      <Icon className="h-3 w-3" />{fmtEur(v)}
    </span>
  );
}

function ScenarioCard({ sc, lang, t }) {
  const m = sc.metrics;
  const label = lang === "it" ? sc.label_it : sc.label_en;
  const rows = [
    { k: "m_ricavi", v: m.ricavi, d: sc.delta.ricavi },
    { k: "m_ebitda", v: m.mol_ebitda, d: sc.delta.mol_ebitda },
    { k: "m_ebit", v: m.ebit, d: sc.delta.ebit },
    { k: "m_utile", v: m.utile_netto, d: sc.delta.utile_netto },
  ];
  const isBase = sc.key === "base";
  return (
    <div data-testid={`scenario-${sc.key}`}
      className={`rounded-xl border p-5 ${isBase ? "border-slate-700 bg-slate-900" : "border-slate-800 bg-slate-900"}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading text-sm font-semibold text-white">{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono-fin text-sm text-slate-400">{m.score}</span>
          <GradePill grade={m.grade} />
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.k} className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{t(row.k)}</span>
            <div className="flex items-center gap-2">
              <span className="font-mono-fin text-xs text-slate-100">{fmtEur(row.v)}</span>
              {!isBase && <DeltaChip v={row.d} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function McBar({ label, value, danger }) {
  const color = danger ? (value >= 40 ? "#EF4444" : value >= 15 ? "#F59E0B" : "#10B981")
                       : "#2563EB";
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-slate-300">{label}</span>
        <span className="font-mono-fin text-sm font-semibold text-white">{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, value)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

const SLIDERS = [
  { key: "revenue_pct", tk: "sim_revenue", min: -30, max: 30, step: 1, unit: "%" },
  { key: "cost_pct", tk: "sim_cost", min: -20, max: 20, step: 1, unit: "%" },
  { key: "rate_bps", tk: "sim_rate", min: 0, max: 500, step: 25, unit: " bps" },
  { key: "dso_days", tk: "sim_dso", min: -30, max: 90, step: 5, unit: " gg" },
];

export function SimulationPanel({ analysisId }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [shocks, setShocks] = useState({ revenue_pct: 0, cost_pct: 0, rate_bps: 0, dso_days: 0 });
  const [calc, setCalc] = useState(false);

  useEffect(() => {
    api.post(`/analyses/${analysisId}/simulate`, { run_monte_carlo: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error(lang === "it" ? "Errore nel caricamento delle simulazioni" : "Failed to load simulations"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const compute = useCallback(async () => {
    setCalc(true);
    try {
      const { data: d } = await api.post(`/analyses/${analysisId}/simulate`,
        { custom: shocks, run_monte_carlo: false });
      setData((prev) => ({ ...prev, custom: d.custom }));
    } catch {
      toast.error(lang === "it" ? "Errore nel calcolo dell'impatto" : "Failed to compute impact");
    } finally { setCalc(false); }
  }, [analysisId, shocks, lang]);

  if (!data) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-10 flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-slate-800 border-t-blue-600 animate-spin" />
      </div>
    );
  }

  const mc = data.monte_carlo;
  const custom = data.custom;

  return (
    <div className="space-y-6" data-testid="simulation-panel">
      {/* Scenarios */}
      <div>
        <p className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-3">{t("sim_scenarios")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.scenarios.map((sc) => <ScenarioCard key={sc.key} sc={sc} lang={lang} t={t} />)}
        </div>
        <div className="mt-4">
          <ScenarioScoreChart scenarios={data.scenarios} lang={lang} t={t} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* What-if */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <p className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-5">{t("sim_whatif")}</p>
          <div className="space-y-5">
            {SLIDERS.map((s) => (
              <div key={s.key}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-300">{t(s.tk)}</span>
                  <span className="font-mono-fin text-sm font-semibold text-white">
                    {shocks[s.key] > 0 && s.unit === "%" ? "+" : ""}{shocks[s.key]}{s.unit}
                  </span>
                </div>
                <Slider data-testid={`slider-${s.key}`} value={[shocks[s.key]]} min={s.min} max={s.max} step={s.step}
                  onValueChange={(v) => setShocks((p) => ({ ...p, [s.key]: v[0] }))} />
              </div>
            ))}
          </div>
          <Button className="mt-6 w-full" onClick={compute} disabled={calc} data-testid="sim-compute-btn">
            <Activity className="h-4 w-4 mr-2" />{calc ? t("sim_calculating") : t("sim_calc")}
          </Button>

          {custom && (
            <div className="mt-5 rounded-lg bg-slate-900 border border-slate-800 p-4" data-testid="sim-custom-result">
              <p className="text-xs font-semibold text-slate-400 mb-3">{t("sim_impact")}</p>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-300">{t("m_score")}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono-fin text-sm text-slate-100">{custom.metrics.score}</span>
                  <GradePill grade={custom.metrics.grade} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[["m_ebitda", custom.metrics.mol_ebitda, custom.delta.mol_ebitda],
                  ["m_ebit", custom.metrics.ebit, custom.delta.ebit],
                  ["m_utile", custom.metrics.utile_netto, custom.delta.utile_netto],
                  ["m_pfn", custom.metrics.pfn, custom.delta.pfn]].map(([k, v, d]) => (
                  <div key={k} className="flex flex-col">
                    <span className="text-[11px] text-slate-500">{t(k)}</span>
                    <span className="font-mono-fin text-sm text-slate-100">{fmtEur(v)}</span>
                    <DeltaChip v={d} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Monte Carlo */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <p className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1">{t("sim_montecarlo")}</p>
          <p className="text-xs text-slate-500 mb-5">{t("sim_mc_sub")}</p>
          {mc && (
            <div className="space-y-4" data-testid="monte-carlo">
              <McBar label={t("mc_neg_income")} value={mc.prob_negative_income} danger />
              <McBar label={t("mc_liq")} value={mc.prob_liquidity_breach} danger />
              <McBar label={t("mc_cov")} value={mc.prob_coverage_breach} danger />
              <McBar label={t("mc_downgrade")} value={mc.prob_downgrade} danger />
              <div className="pt-3 border-t border-slate-800">
                <p className="text-xs text-slate-400 mb-2">{t("mc_score_range")}</p>
                <div className="flex items-center gap-3">
                  <span className="font-mono-fin text-sm text-red-600">{mc.score_p5}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-400" />
                  <span className="font-mono-fin text-sm text-white font-semibold">{mc.score_p50}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500" />
                  <span className="font-mono-fin text-sm text-emerald-600">{mc.score_p95}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
